import React, { useCallback, useRef } from 'react';
import Tooltip from './Tooltip';

export interface TruncatedTooltipProps {
    text: React.ReactNode | string;
    children: React.ReactNode;
    delay?: number;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    className?: string;
}

/**
 * TruncatedTooltip renders its children in a single-line truncating container
 * (overflow:hidden + text-overflow:ellipsis + white-space:nowrap) and surfaces
 * the malachite Tooltip only when the content is actually being cut off.
 *
 * Truncation is checked at interaction time (Tooltip's `shouldShow`), not at
 * mount: a mount-time measurement forces a synchronous layout per instance,
 * and in large tables those reads interleave with other components' DOM writes
 * — hundreds of full layout passes per mounted batch (the Safari row-mount
 * stalls of ENG-1168). The ellipsis itself is pure CSS and needs no JS.
 */
const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
    text,
    children,
    delay = 0.5,
    placement = 'top',
    className = '',
}) => {
    const containerRef = useRef<HTMLSpanElement | null>(null);

    // One layout read, on the user's hover/focus — never on the mount path.
    const isTruncated = useCallback(() => {
        const el = containerRef.current;
        return !!el && el.scrollWidth > el.clientWidth;
    }, []);

    return (
        <Tooltip
            text={text}
            delay={delay}
            placement={placement}
            wrapChildren={false}
            isShrinkable
            shouldShow={isTruncated}
        >
            <span
                ref={containerRef}
                className={`block w-full overflow-hidden whitespace-nowrap text-ellipsis ${className}`.trim()}
            >
                {children}
            </span>
        </Tooltip>
    );
};

export default TruncatedTooltip;
